// The Access screen (SEC2, SEC15, SEC16): access levels and the Entra groups mapped to them. Edits
// collect in a draft and save together. The API enforces every rule; the screen shows them first:
// some group must keep access.admin, you must keep it yourself, and granting comp.read or
// access.admin to a group asks twice. Unmapping a group asks first (Chris, 2026-10-15: the Base Camp
// Testers mapping was removed by accident). "View as" lets an admin see the app as a person or a level.

import { useEffect, useState } from "react";
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Select,
  Subtitle2,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "@fluentui/react-components";
import { needsSecondConfirmation, newLevelId, resolveAccess, validateAccessConfig, type AccessConfig, type AccessLevel, type Capability, type DataScope } from "../../../core";
import { errorText, send, startViewAs, useApi, type AccessResponse, type ViewAs } from "../api";
import { when } from "../format";
import { ErrorBar, Loading, Page, Warnings } from "../ui";

export function Access() {
  const data = useApi<AccessResponse>("/access");
  const [draft, setDraft] = useState<AccessConfig | null>(null);
  const [editing, setEditing] = useState<AccessLevel | "new" | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);
  const [unmapping, setUnmapping] = useState<number | null>(null);

  useEffect(() => {
    if (data.data) setDraft({ levels: data.data.levels, grants: data.data.grants });
  }, [data.data]);

  const d = data.data;
  if (!d || !draft) return <Page title="Access">{data.loading ? <Loading /> : <ErrorBar message={data.error} />}</Page>;

  const original: AccessConfig = { levels: d.levels, grants: d.grants };
  const dirty = JSON.stringify(original) !== JSON.stringify(draft);
  const problems = validateAccessConfig(draft);
  const keepsMine = resolveAccess(d.yourGroups, draft).capabilities.has("access.admin");
  if (!keepsMine) problems.push("You would lose access.admin yourself. Keep one of your own groups on a level with access.admin.");
  const levelName = (id: string) => draft.levels.find((l) => l.id === id)?.name ?? id;
  const scopeText = (s: DataScope) => d.scopes.find((x) => x.id === s)?.text ?? s;

  async function save(confirm: boolean) {
    setBusy(true);
    setError(undefined);
    try {
      await send("PUT", "/access", { config: draft, version: d!.version, confirm });
      setConfirming(false);
      setSaved(true);
      data.reload();
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }
  const change = (next: AccessConfig) => {
    setDraft(next);
    setSaved(false);
  };

  return (
    <Page
      title="Access"
      actions={
        <>
          {dirty && <Button onClick={() => change(original)}>Discard changes</Button>}
          <Button appearance="primary" disabled={!dirty || busy || problems.length > 0} onClick={() => (needsSecondConfirmation(original, draft) ? setConfirming(true) : void save(false))}>
            {busy ? "Saving…" : dirty ? "Save changes" : saved ? "Saved" : "No changes"}
          </Button>
        </>
      }
    >
      <Caption1>
        A level is a set of capabilities and what data it sees. People get a level by being in an Entra group mapped to it; someone in
        several groups gets everything those levels allow. Add a person in Entra; add a level here and point a group at it.
      </Caption1>
      <ErrorBar message={error} />
      {dirty && <Warnings items={problems} />}

      <ViewAsPanel />

      <Card className="panel wide">
        <div className="panel-head">
          <Subtitle2>Levels</Subtitle2>
          <Button size="small" onClick={() => setEditing("new")}>
            New level
          </Button>
        </div>
        <Table size="small" className="data-table" aria-label="Access levels">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Level</TableHeaderCell>
              <TableHeaderCell>Sees</TableHeaderCell>
              <TableHeaderCell>Capabilities</TableHeaderCell>
              <TableHeaderCell>Groups</TableHeaderCell>
              <TableHeaderCell aria-label="Actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {draft.levels.map((l) => {
              const groups = draft.grants.filter((g) => g.levelId === l.id).length;
              return (
                <TableRow key={l.id || l.name}>
                  <TableCell>
                    <strong>{l.name}</strong>
                  </TableCell>
                  <TableCell>{scopeText(l.scope)}</TableCell>
                  <TableCell>
                    <span className="caps cell-caps">
                      {l.capabilities.map((c) => (
                        <Badge key={c} appearance="tint" color={c === "comp.read" || c === "access.admin" ? "warning" : "informative"} size="small">
                          {c}
                        </Badge>
                      ))}
                    </span>
                  </TableCell>
                  <TableCell>{groups}</TableCell>
                  <TableCell>
                    <span className="panel-tools">
                      <Button size="small" appearance="subtle" onClick={() => setEditing(l)}>
                        Edit…
                      </Button>
                      <Button
                        size="small"
                        appearance="subtle"
                        disabled={groups > 0}
                        title={groups > 0 ? "Unmap its groups first." : undefined}
                        onClick={() => change({ ...draft, levels: draft.levels.filter((x) => x !== l) })}
                      >
                        Remove
                      </Button>
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Card className="panel wide">
        <Subtitle2>Groups</Subtitle2>
        <Caption1>
          An Entra security group's object ID grants its level. The label is only for this screen. Your own groups:{" "}
          {d.yourGroups.length ? d.yourGroups.join(", ") : "none in your token"}.
        </Caption1>
        <Table size="small" className="data-table" aria-label="Group mappings">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Group</TableHeaderCell>
              <TableHeaderCell>Object ID</TableHeaderCell>
              <TableHeaderCell>Level</TableHeaderCell>
              <TableHeaderCell aria-label="Actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {draft.grants.map((g, i) => (
              <TableRow key={`${g.groupId}|${i}`}>
                <TableCell>
                  <Input
                    size="small"
                    value={g.label ?? ""}
                    placeholder="Label"
                    aria-label={`Label for group ${g.groupId}`}
                    onChange={(_, x) => change({ ...draft, grants: draft.grants.map((y, j) => (j === i ? { ...y, label: x.value || undefined } : y)) })}
                  />
                  {d.yourGroups.includes(g.groupId) && (
                    <Badge appearance="outline" size="small" className="in-effect">
                      You
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <code>{g.groupId}</code>
                </TableCell>
                <TableCell>
                  <Select
                    size="small"
                    value={g.levelId}
                    aria-label={`Level for group ${g.label ?? g.groupId}`}
                    onChange={(_, x) => change({ ...draft, grants: draft.grants.map((y, j) => (j === i ? { ...y, levelId: x.value } : y)) })}
                  >
                    {draft.levels.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  <Button size="small" appearance="subtle" onClick={() => setUnmapping(i)}>
                    Unmap
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <AddGrant levels={draft.levels} onAdd={(g) => change({ ...draft, grants: [...draft.grants, g] })} />
      </Card>

      <Card className="panel wide">
        <Subtitle2>History</Subtitle2>
        {d.history.length === 0 ? (
          <Body1 className="muted">No changes yet.</Body1>
        ) : (
          <ul className="history">
            {d.history.map((h, i) => (
              <li key={i}>
                <Caption1>
                  {when(h.at)} · {h.who}
                </Caption1>
                <Body1>{h.summary}</Body1>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {editing && (
        <LevelDialog
          level={editing === "new" ? null : editing}
          catalog={d.catalog}
          scopes={d.scopes}
          onClose={() => setEditing(null)}
          onSave={(l) => {
            const added = { ...l, id: newLevelId(l.name, new Set(draft.levels.map((x) => x.id))) };
            change({ ...draft, levels: editing === "new" ? [...draft.levels, added] : draft.levels.map((x) => (x === editing ? l : x)) });
            setEditing(null);
          }}
        />
      )}
      {unmapping !== null && draft.grants[unmapping] && (
        <UnmapDialog
          group={draft.grants[unmapping].label ?? draft.grants[unmapping].groupId}
          level={levelName(draft.grants[unmapping].levelId)}
          yours={d.yourGroups.includes(draft.grants[unmapping].groupId)}
          onCancel={() => setUnmapping(null)}
          onConfirm={() => {
            change({ ...draft, grants: draft.grants.filter((_, j) => j !== unmapping) });
            setUnmapping(null);
          }}
        />
      )}
      {confirming && (
        <Dialog open onOpenChange={(_, x) => !x.open && setConfirming(false)}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>Give a group pay visibility or access administration?</DialogTitle>
              <DialogContent className="dialog-stack">
                <Body1>
                  This change gives at least one group <strong>comp.read</strong> (everyone's pay and per-person rates) or <strong>access.admin</strong> (this
                  screen) that it didn't have. Everyone in those groups gets it as soon as you save.
                </Body1>
                <ErrorBar message={error} />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setConfirming(false)}>Cancel</Button>
                <Button appearance="primary" disabled={busy} onClick={() => void save(true)}>
                  {busy ? "Saving…" : "Yes, save"}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </Page>
  );
}

function AddGrant({ levels, onAdd }: { levels: AccessLevel[]; onAdd: (g: { groupId: string; levelId: string; label?: string }) => void }) {
  const [groupId, setGroupId] = useState("");
  const [label, setLabel] = useState("");
  const [levelId, setLevelId] = useState(levels.find((l) => l.id === "viewer")?.id ?? levels[0]?.id ?? "");
  const valid = groupId.trim().length > 0 && groupId.trim().length <= 64 && !!levelId;
  return (
    <div className="toolbar">
      <Input size="small" value={groupId} onChange={(_, x) => setGroupId(x.value)} placeholder="Group object ID" aria-label="New group's object ID" className="group-input" />
      <Input size="small" value={label} onChange={(_, x) => setLabel(x.value)} placeholder="Label, e.g. Finance admins" aria-label="New group's label" />
      <Select size="small" value={levelId} onChange={(_, x) => setLevelId(x.value)} aria-label="New group's level">
        {levels.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
          </option>
        ))}
      </Select>
      <Button
        size="small"
        disabled={!valid}
        onClick={() => {
          onAdd({ groupId: groupId.trim(), levelId, ...(label.trim() ? { label: label.trim() } : {}) });
          setGroupId("");
          setLabel("");
        }}
      >
        Map group
      </Button>
    </div>
  );
}

function LevelDialog(props: { level: AccessLevel | null; catalog: AccessResponse["catalog"]; scopes: AccessResponse["scopes"]; onClose: () => void; onSave: (l: AccessLevel) => void }) {
  const [name, setName] = useState(props.level?.name ?? "");
  const [scope, setScope] = useState<DataScope>(props.level?.scope ?? "all");
  const [caps, setCaps] = useState<Set<Capability>>(new Set(props.level?.capabilities ?? ["self.read"]));
  const toggle = (c: Capability, on: boolean) => {
    // QuickBooks figures are for the Admin level only (SEC20): ask before widening them.
    if (on && c === "books.read" && !window.confirm("books.read shows every QuickBooks figure, including each partner's and contractor's pay. The PRD keeps it to the Admin level. Add it to this level anyway?")) return;
    const next = new Set(caps);
    if (on) next.add(c);
    else next.delete(c);
    setCaps(next);
  };
  return (
    <Dialog open onOpenChange={(_, x) => !x.open && props.onClose()}>
      <DialogSurface className="dialog-wide">
        <DialogBody>
          <DialogTitle>{props.level ? `Edit ${props.level.name}` : "New level"}</DialogTitle>
          <DialogContent className="dialog-stack">
            <div className="form-grid">
              <Field label="Name" required>
                <Input value={name} onChange={(_, x) => setName(x.value)} autoFocus />
              </Field>
              <Field label="Sees">
                <Select value={scope} onChange={(_, x) => setScope(x.value as DataScope)}>
                  {props.scopes.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.text}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <Caption1>Capabilities</Caption1>
            <div className="cap-list">
              {props.catalog.map((c) => (
                <Checkbox key={c.id} checked={caps.has(c.id)} onChange={(_, x) => toggle(c.id, !!x.checked)} label={<><strong>{c.id}</strong> · {c.grants}</>} />
              ))}
            </div>
            {(caps.has("comp.read") || caps.has("access.admin")) && (
              <MessageBar intent="warning">
                <MessageBarBody>comp.read shows everyone's pay; access.admin opens this screen. Saving asks you to confirm if a group gains either.</MessageBarBody>
              </MessageBar>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={props.onClose}>Cancel</Button>
            <Button appearance="primary" disabled={!name.trim()} onClick={() => props.onSave({ id: props.level?.id ?? "", name: name.trim(), scope, capabilities: [...caps] })}>
              {props.level ? "Done" : "Add level"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function UnmapDialog(props: { group: string; level: string; yours: boolean; onCancel: () => void; onConfirm: () => void }) {
  return (
    <Dialog open onOpenChange={(_, x) => !x.open && props.onCancel()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Unmap {props.group}?</DialogTitle>
          <DialogContent className="dialog-stack">
            <Body1>
              Everyone in <strong>{props.group}</strong> loses the <strong>{props.level}</strong> level when you save, unless another of their groups grants it.
              {props.yours ? " You are in this group." : ""} Until you save, Discard changes puts it back.
            </Body1>
          </DialogContent>
          <DialogActions>
            <Button onClick={props.onCancel}>Keep it</Button>
            <Button appearance="primary" onClick={props.onConfirm}>
              Unmap
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

interface ViewAsOptions {
  people: { id: string; name: string; signedIn: boolean; hasEmail: boolean; levels: string[] }[];
  levels: { id: string; name: string }[];
}

/** "" views a person at the groups they had at their last sign-in. */
const THEIR_GROUPS = "";

/**
 * Pick a person or a level and see Base Camp as they would, read-only. A person can be viewed at a
 * level you pick (Chris, 2026-10-19), so you can check what they will see before giving them access
 * or before they have ever signed in.
 */
function ViewAsPanel() {
  const options = useApi<ViewAsOptions>("/view-as/options");
  const [kind, setKind] = useState<ViewAs["kind"]>("person");
  const [id, setId] = useState("");
  const [levelId, setLevelId] = useState(THEIR_GROUPS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const o = options.data;
  const person = kind === "person" ? o?.people.find((p) => p.id === id) : undefined;
  const defaultLevel = (signedIn: boolean) => (signedIn ? THEIR_GROUPS : (o?.levels.find((l) => l.id === "member")?.id ?? o?.levels[0]?.id ?? ""));
  const ready = !!id && (kind === "level" || (!!person && person.hasEmail && (person.signedIn || levelId !== THEIR_GROUPS)));

  async function start() {
    setBusy(true);
    setError(undefined);
    try {
      await startViewAs(kind, id, kind === "person" && levelId !== THEIR_GROUPS ? levelId : undefined);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Card className="panel wide">
      <Subtitle2>View as</Subtitle2>
      <Caption1>
        See Base Camp exactly as a person sees it (their Home, screens and data, including only their own pay) or as an access level. It is read-only while
        you view, and each start and stop goes in the history below. Pick the level to view a person at, to check what they will see before you give
        them access or before they have signed in; or view them at the groups they had at their last sign-in.
      </Caption1>
      {!o ? (
        options.loading ? <Loading /> : <ErrorBar message={options.error} />
      ) : (
        <div className="toolbar">
          <Select
            size="small"
            value={kind}
            aria-label="View as a person or a level"
            onChange={(_, x) => {
              setKind(x.value as ViewAs["kind"]);
              setId("");
              setError(undefined);
            }}
          >
            <option value="person">Person</option>
            <option value="level">Access level</option>
          </Select>
          <Select
            size="small"
            value={id}
            aria-label={kind === "person" ? "Person to view as" : "Level to view as"}
            onChange={(_, x) => {
              setId(x.value);
              setError(undefined);
              const p = o.people.find((q) => q.id === x.value);
              if (kind === "person" && p) setLevelId(defaultLevel(p.signedIn));
            }}
          >
            <option value="">{kind === "person" ? "Choose a person…" : "Choose a level…"}</option>
            {kind === "person"
              ? o.people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.signedIn ? p.name : `${p.name} (not signed in yet)`}
                  </option>
                ))
              : o.levels.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
          </Select>
          {person && (
            <Select size="small" value={levelId} aria-label={`Level to view ${person.name} at`} onChange={(_, x) => (setLevelId(x.value), setError(undefined))}>
              {person.signedIn && <option value={THEIR_GROUPS}>Their groups{person.levels.length ? ` (${person.levels.join(", ")})` : ""}</option>}
              {o.levels.map((l) => (
                <option key={l.id} value={l.id}>
                  At the {l.name} level
                </option>
              ))}
            </Select>
          )}
          <Button size="small" appearance="primary" disabled={!ready || busy} onClick={() => void start()}>
            {busy ? "Opening…" : "View as"}
          </Button>
        </div>
      )}
      {person && !person.hasEmail && <Caption1>{person.name} has no 3Seven account on their person record, so there is nothing to view as. Add their email on their card first.</Caption1>}
      {person && person.hasEmail && !person.signedIn && (
        <Caption1>{person.name} hasn't signed in yet, so you choose the level: you see their own data with exactly what that level allows.</Caption1>
      )}
      <ErrorBar message={error} />
    </Card>
  );
}
